/**
 * High Finishes 100–170 (Marcel 2026-10-08) – REINE Regeln, 1:1-Port von
 * Backend helper/high-finish-helper.js (Paritätstest highFinishes.test.ts mit denselben Fällen).
 * Weg = 1–3 Darts, Summe = Finish, letzter Dart Doppel oder Bull; je Weg optional ein Bild (Dateiname).
 */

export const IMPOSSIBLE_FINISHES: readonly number[] = Object.freeze([159, 162, 163, 165, 166, 168, 169]);
export const MIN_FINISH = 100;
export const MAX_FINISH = 170;
export const VALID_FINISHES: readonly number[] = Object.freeze(
  Array.from({ length: MAX_FINISH - MIN_FINISH + 1 }, (_, i) => MIN_FINISH + i).filter((f) => !IMPOSSIBLE_FINISHES.includes(f)),
);
export const MAX_ROUTES_PER_FINISH = 5;
export const MAX_ROUTE_LENGTH = 40;
export const MAX_DARTS = 3;

export interface HighFinishRoute { id: string | null; route: string | null; image: string | null; createdAt: number | null }
export interface HighFinish { finish: number; achieved: boolean; routes: HighFinishRoute[]; updatedAt?: number | null }

export const isValidFinish = (n: unknown): n is number => Number.isInteger(n) && VALID_FINISHES.includes(n as number);

export interface ParsedDart { value: number; double: boolean; text: string }

export function parseDart(token: unknown): ParsedDart | null {
  const t = String(token ?? '').trim().toUpperCase().replace(/^DB$/, 'BULL').replace(/^SB$/, '25');
  if (!t) return null;
  if (t === 'BULL' || t === 'B' || t === '50') return { value: 50, double: true, text: 'Bull' };
  if (t === '25' || t === 'OUTER') return { value: 25, double: false, text: '25' };
  const m = /^([TDS]?)(\d{1,2})$/.exec(t);
  if (!m) return null;
  const n = parseInt(m[2], 10);
  if (n < 1 || n > 20) return null;
  const mult = m[1] === 'T' ? 3 : m[1] === 'D' ? 2 : 1;
  return { value: n * mult, double: m[1] === 'D', text: `${m[1] || 'S'}${n}` };
}

export type RouteCheck = { ok: true; route: string; darts: number } | { ok: false; reason: string };

export function validateRoute(text: unknown, finish: number): RouteCheck {
  if (!isValidFinish(finish)) return { ok: false, reason: 'Ungültiges Finish' };
  const raw = String(text ?? '').trim();
  if (!raw) return { ok: false, reason: 'Weg fehlt' };
  if (raw.length > MAX_ROUTE_LENGTH) return { ok: false, reason: `Weg zu lang (max. ${MAX_ROUTE_LENGTH} Zeichen)` };
  const tokens = raw.split(/[\s,+\-–>→/]+/).filter(Boolean);
  if (tokens.length === 0 || tokens.length > MAX_DARTS) return { ok: false, reason: `Ein Weg hat 1 bis ${MAX_DARTS} Darts` };
  const darts = tokens.map(parseDart);
  const badIdx = darts.findIndex((d) => d === null);
  if (badIdx >= 0) return { ok: false, reason: `Unbekannter Dart „${tokens[badIdx]}“ – erlaubt sind z. B. T20, D12, S5, Bull, 25` };
  const list = darts as ParsedDart[];
  const sum = list.reduce((s, d) => s + d.value, 0);
  if (sum !== finish) return { ok: false, reason: `Der Weg ergibt ${sum}, nicht ${finish}` };
  if (!list[list.length - 1].double) return { ok: false, reason: 'Der letzte Dart muss ein Doppel oder Bull sein' };
  return { ok: true, route: list.map((d) => d.text).join(' '), darts: list.length };
}

/** Suche: Zahl-Teilstring („17“ → 117, 170); leer = alle */
export function filterFinishes(query: unknown, finishes: readonly number[] = VALID_FINISHES): number[] {
  const q = String(query ?? '').replace(/\D/g, '');
  if (!q) return [...finishes];
  return finishes.filter((f) => String(f).includes(q));
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Defensives Parsen der Backend-Liste (fehlt/kaputt → []) */
export function parseHighFinishes(raw: unknown): HighFinish[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((h: any) => h && typeof h === 'object' && isValidFinish(Number(h.finish)))
    .map((h: any) => ({
      finish: Number(h.finish),
      achieved: h.achieved !== false,
      routes: (Array.isArray(h.routes) ? h.routes : []).map((r: any) => ({ id: str(r?.id), route: str(r?.route), image: str(r?.image), createdAt: num(r?.createdAt) })).filter((r: HighFinishRoute) => r.route),
      updatedAt: num(h.updatedAt),
    }))
    .sort((a, b) => a.finish - b.finish);
}

/** Eintrag eines Finishs holen (oder leer) */
export const findFinish = (list: HighFinish[], finish: number): HighFinish | undefined => list.find((h) => h.finish === finish);

/** Mindestens ein geschafftes Finish? Ohne Haken bleibt der Block im Profil-Tab komplett weg (Marcel 2026-10-09). */
export const hasAchievedHighFinishes = (list: HighFinish[] | null | undefined): boolean =>
  Array.isArray(list) && list.some((h) => h && h.achieved !== false);

/** PURE: Haken setzen/entfernen; ohne Haken und ohne Wege verschwindet der Eintrag */
export function setAchieved(list: HighFinish[], finish: number, achieved: boolean): HighFinish[] {
  const existing = findFinish(list, finish);
  const next: HighFinish = { finish, achieved, routes: existing?.routes ?? [], updatedAt: Date.now() };
  const rest = list.filter((h) => h.finish !== finish);
  if (!achieved && next.routes.length === 0) return rest.sort((a, b) => a.finish - b.finish);
  return [...rest, next].sort((a, b) => a.finish - b.finish);
}

/** PURE: Weg hinzufügen (prüft, normalisiert, Dublette ignoriert, setzt den Haken) */
export function addRoute(list: HighFinish[], finish: number, text: string, image: string | null = null): { list: HighFinish[]; error: string | null } {
  const check = validateRoute(text, finish);
  if (!check.ok) return { list, error: check.reason };
  const existing = findFinish(list, finish);
  const routes = existing?.routes ?? [];
  if (routes.some((r) => r.route === check.route)) return { list, error: 'Diesen Weg hast du schon eingetragen' };
  if (routes.length >= MAX_ROUTES_PER_FINISH) return { list, error: `Maximal ${MAX_ROUTES_PER_FINISH} Wege je Finish` };
  const next: HighFinish = { finish, achieved: true, routes: [...routes, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, route: check.route, image, createdAt: Date.now() }], updatedAt: Date.now() };
  return { list: [...list.filter((h) => h.finish !== finish), next].sort((a, b) => a.finish - b.finish), error: null };
}

export function removeRoute(list: HighFinish[], finish: number, routeId: string): HighFinish[] {
  return list.map((h) => (h.finish === finish ? { ...h, routes: h.routes.filter((r) => r.id !== routeId) } : h));
}

export function setRouteImage(list: HighFinish[], finish: number, routeId: string, image: string | null): HighFinish[] {
  return list.map((h) => (h.finish === finish ? { ...h, routes: h.routes.map((r) => (r.id === routeId ? { ...r, image } : r)) } : h));
}

/** Payload fürs Backend (temporäre Ids weglassen → Backend vergibt neue) */
export function toPayload(list: HighFinish[]): { finish: number; achieved: boolean; routes: { id?: string; route: string; image: string | null }[] }[] {
  return list.map((h) => ({ finish: h.finish, achieved: h.achieved, routes: h.routes.filter((r) => r.route).map((r) => ({ ...(r.id && !r.id.startsWith('tmp-') ? { id: r.id } : {}), route: r.route as string, image: r.image })) }));
}

/** Anzahl geschaffter Finishes (für Badges/Überschriften) */
export const achievedCount = (list: HighFinish[]): number => list.filter((h) => h.achieved).length;
